import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SummarizerTimeRange } from '@/services/summarizerAgentService';
import styles from '@/styles/components/tools/SummarizerSettingsBar.module.css';
import chatStyles from '@/styles/components/chat/SettingsBar.module.css';

export interface SummarizerSettings {
  lang: string;
  region: string;
  timeRange: SummarizerTimeRange;
}

interface SummarizerSettingsBarProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRight?: number;
  anchorBottom?: number;
  value: SummarizerSettings;
  onChange: (next: SummarizerSettings) => void;
}

const LOCALE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'af', label: 'Afrikaans' },
  { value: 'ar', label: 'العربية (Arabic)' },
  { value: 'bg', label: 'Български (Bulgarian)' },
  { value: 'bn', label: 'বাংলা (Bangla)' },
  { value: 'bo', label: 'བོད་སྐད་ (Tibetan)' },
  { value: 'ca', label: 'Català (Catalan)' },
  { value: 'cs', label: 'Čeština (Czech)' },
  { value: 'cy', label: 'Cymraeg (Welsh)' },
  { value: 'da', label: 'Dansk (Danish)' },
  { value: 'de', label: 'Deutsch (German)' },
  { value: 'dv', label: 'ދިވެހި (Dhivehi)' },
  { value: 'el-GR', label: 'Ελληνικά, Ελλάδα (Greek, Greece)' },
  { value: 'en', label: 'English' },
  { value: 'eo', label: 'Esperanto' },
  { value: 'es', label: 'Español (Spanish)' },
  { value: 'et', label: 'Eesti (Estonian)' },
  { value: 'eu', label: 'Euskara (Basque)' },
  { value: 'fa-IR', label: 'فارسی, ایران (Persian, Iran)' },
  { value: 'fi', label: 'Suomi (Finnish)' },
  { value: 'fil', label: 'Filipino' },
  { value: 'fr', label: 'Français (French)' },
  { value: 'ga', label: 'Gaeilge (Irish)' },
  { value: 'gl', label: 'Galego (Galician)' },
  { value: 'he', label: 'עברית (Hebrew)' },
  { value: 'hr', label: 'Hrvatski (Croatian)' },
  { value: 'hu', label: 'Magyar (Hungarian)' },
  { value: 'ia', label: 'Interlingua' },
  { value: 'id', label: 'Indonesia (Indonesian)' },
  { value: 'it', label: 'Italiano (Italian)' },
  { value: 'ja', label: '日本語 (Japanese)' },
  { value: 'ko', label: '한국어 (Korean)' },
  { value: 'lt', label: 'Lietuvių (Lithuanian)' },
  { value: 'lv', label: 'Latviešu (Latvian)' },
  { value: 'ml', label: 'മലയാളം (Malayalam)' },
  { value: 'ms', label: 'Melayu (Malay)' },
  { value: 'nb-NO', label: 'Norsk bokmål, Norge (Norwegian bokmål, Norway)' },
  { value: 'nl', label: 'Nederlands (Dutch)' },
  { value: 'nl-BE', label: 'Nederlands, België (Dutch, Belgium)' },
  { value: 'oc', label: 'Occitan' },
  { value: 'pa', label: 'ਪੰਜਾਬੀ (Punjabi)' },
  { value: 'pap', label: 'Papiamento' },
  { value: 'pl', label: 'Polski (Polish)' },
  { value: 'pt', label: 'Português (Portuguese)' },
  { value: 'pt-BR', label: 'Português, Brasil (Portuguese, Brazil)' },
  { value: 'ro', label: 'Română (Romanian)' },
  { value: 'ru', label: 'Русский (Russian)' },
  { value: 'si', label: 'සිංහල (Sinhala)' },
  { value: 'sk', label: 'Slovenčina (Slovak)' },
  { value: 'sl', label: 'Slovenščina (Slovenian)' },
  { value: 'sr', label: 'Српски (Serbian)' },
  { value: 'sv', label: 'Svenska (Swedish)' },
  { value: 'szl', label: 'Ślōnski (Silesian)' },
  { value: 'ta', label: 'தமிழ் (Tamil)' },
  { value: 'te', label: 'తెలుగు (Telugu)' },
  { value: 'th', label: 'ไทย (Thai)' },
  { value: 'tr', label: 'Türkçe (Turkish)' },
  { value: 'tt', label: 'Татар (Tatar)' },
  { value: 'uk', label: 'Українська (Ukrainian)' },
  { value: 'vi', label: 'Tiếng việt (Vietnamese)' },
  { value: 'zh-HK', label: '中文, 中國香港特別行政區 (Chinese, Hong Kong SAR China)' },
  { value: 'zh-Hans-CN', label: '中文, 中国 (Chinese, China)' },
  { value: 'zh-Hant-TW', label: '中文, 台灣 (Chinese, Taiwan)' },
];


const TIME_RANGE_OPTIONS: Array<{ value: SummarizerTimeRange; label: string }> =
  [
    { value: 'day', label: 'Past 24 Hours' },
    { value: 'week', label: 'Past Week' },
    { value: 'month', label: 'Past Month' },
    { value: 'year', label: 'Past Year' },
  ];

const SummarizerSettingsBar: React.FC<SummarizerSettingsBarProps> = ({
  isOpen,
  onClose,
  anchorRight,
  anchorBottom,
  value,
  onChange,
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const regionDropdownRef = useRef<HTMLDivElement>(null);
  const timeDropdownRef = useRef<HTMLDivElement>(null);
  const [local, setLocal] = useState<SummarizerSettings>(value);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isRegionOpen, setIsRegionOpen] = useState(false);
  const [isTimeOpen, setIsTimeOpen] = useState(false);

  useEffect(() => {
    if (isOpen) setLocal(value);
  }, [isOpen, value]);

  const hasChanges = useMemo(() => {
    return (
      local.lang !== value.lang ||
      local.region !== value.region ||
      local.timeRange !== value.timeRange
    );
  }, [local, value]);

  const applyAndClose = useCallback(() => {
    if (hasChanges) onChange(local);
    onClose();
  }, [hasChanges, local, onChange, onClose]);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (!barRef.current) return;
      const target = e.target as Node;
      if (!barRef.current.contains(target)) {
        applyAndClose();
        return;
      }

      if (isLangOpen && langDropdownRef.current && !langDropdownRef.current.contains(target)) {
        setIsLangOpen(false);
      }
      if (
        isRegionOpen &&
        regionDropdownRef.current &&
        !regionDropdownRef.current.contains(target)
      ) {
        setIsRegionOpen(false);
      }
      if (isTimeOpen && timeDropdownRef.current && !timeDropdownRef.current.contains(target)) {
        setIsTimeOpen(false);
      }
    },
    [applyAndClose, isLangOpen, isRegionOpen, isTimeOpen]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        applyAndClose();
      }
    },
    [applyAndClose]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClickOutside, handleKeyDown]);

  if (!isOpen) return null;

  const langLabel =
    LOCALE_OPTIONS.find(o => o.value === local.lang)?.label || local.lang;
  const regionLabel =
    LOCALE_OPTIONS.find(o => o.value === local.region)?.label || local.region;
  const timeLabel =
    TIME_RANGE_OPTIONS.find(o => o.value === local.timeRange)?.label ||
    local.timeRange;

  return (
    <div
      ref={barRef}
      className={styles.settingsBar}
      style={{
        right: anchorRight !== undefined ? `${anchorRight}px` : undefined,
        bottom: anchorBottom !== undefined ? `${anchorBottom}px` : undefined,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Summarizer settings"
    >
      <div className={styles.header}>
        <h3 className={styles.title}>Summarizer settings</h3>
        <button
          type="button"
          className={styles.closeButton}
          onClick={applyAndClose}
          aria-label="Close settings"
        >
          ×
        </button>
      </div>

      <div className={styles.body}>
        <div
          className={styles.field}
          style={{ zIndex: isLangOpen ? 300 : 200 }}
        >
          <div className={styles.label}>Language</div>
          <div
            ref={langDropdownRef}
            className={`${chatStyles.dropdownContainer} ${isLangOpen ? chatStyles.open : ''}`}
          >
            <div
              className={chatStyles.sizemdTypedefault}
              onClick={() => {
                setIsLangOpen(v => !v);
                setIsRegionOpen(false);
                setIsTimeOpen(false);
              }}
            >
              <div className={chatStyles.text1}>
                <div className={chatStyles.text2}>{langLabel}</div>
              </div>
              <img
                className={chatStyles.rightIcon}
                alt=""
                src="/icons/chevron_down.svg"
              />
            </div>
            {isLangOpen && (
              <div className={chatStyles.dropdownMenu}>
                {LOCALE_OPTIONS.map(opt => (
                  <div
                    key={opt.value}
                    className={chatStyles.dropdownItem}
                    onClick={() => {
                      setLocal(prev => ({ ...prev, lang: opt.value }));
                      setIsLangOpen(false);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div
          className={styles.field}
          style={{ zIndex: isRegionOpen ? 300 : 150 }}
        >
          <div className={styles.label}>Region</div>
          <div
            ref={regionDropdownRef}
            className={`${chatStyles.dropdownContainer} ${isRegionOpen ? chatStyles.open : ''}`}
          >
            <div
              className={chatStyles.sizemdTypedefault}
              onClick={() => {
                setIsRegionOpen(v => !v);
                setIsLangOpen(false);
                setIsTimeOpen(false);
              }}
            >
              <div className={chatStyles.text1}>
                <div className={chatStyles.text2}>{regionLabel}</div>
              </div>
              <img
                className={chatStyles.rightIcon}
                alt=""
                src="/icons/chevron_down.svg"
              />
            </div>
            {isRegionOpen && (
              <div className={chatStyles.dropdownMenu}>
                {LOCALE_OPTIONS.map(opt => (
                  <div
                    key={opt.value}
                    className={chatStyles.dropdownItem}
                    onClick={() => {
                      setLocal(prev => ({ ...prev, region: opt.value }));
                      setIsRegionOpen(false);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div
          className={styles.field}
          style={{ zIndex: isTimeOpen ? 300 : 100 }}
        >
          <div className={styles.label}>Time range</div>
          <div
            ref={timeDropdownRef}
            className={`${chatStyles.dropdownContainer} ${isTimeOpen ? chatStyles.open : ''}`}
          >
            <div
              className={chatStyles.sizemdTypedefault}
              onClick={() => {
                setIsTimeOpen(v => !v);
                setIsLangOpen(false);
                setIsRegionOpen(false);
              }}
            >
              <div className={chatStyles.text1}>
                <div className={chatStyles.text2}>{timeLabel}</div>
              </div>
              <img
                className={chatStyles.rightIcon}
                alt=""
                src="/icons/chevron_down.svg"
              />
            </div>
            {isTimeOpen && (
              <div className={chatStyles.dropdownMenu}>
                {TIME_RANGE_OPTIONS.map(opt => (
                  <div
                    key={opt.value}
                    className={chatStyles.dropdownItem}
                    onClick={() => {
                      setLocal(prev => ({ ...prev, timeRange: opt.value }));
                      setIsTimeOpen(false);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.applyButton}
          onClick={applyAndClose}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default SummarizerSettingsBar;

